import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, distinctUntilChanged } from 'rxjs/operators';
import { ReusableStreamMethod, SyndicateRole } from '@ipreo/muni-shared';
import { GameDayCalculatedData } from '../../gameday/data/gameDay-data.calculation';
import { GameDayRootReducerState } from '../../state/root.state';
import { getTrancheOrdersFilteredState } from '../../state/data/reducer';
import {
  ManagerOverviewReportWithDefaultData,
  ManagerOverviewReport
} from '../models/manager';
import { GameDayModel } from '../../models/gameday-model';

@Injectable()
export class ManagersOverviewService {
  constructor(private store: Store<GameDayRootReducerState>) {}

  @ReusableStreamMethod
  public getData(): Observable<GameDayCalculatedData> {
    return this.store.pipe(
      select(getTrancheOrdersFilteredState),
      filter(({ orders }) => !!orders),
      distinctUntilChanged()
    );
  }

  @ReusableStreamMethod
  public getManagersOverviewData(): Observable<ManagerOverviewReport[]> {
    return this.getData().pipe(
      map(({ deal: { syndicate }, orders }) => {
        orders.forEach(order => {
          if (!order.syndicateDepartment.role) {
            order.syndicateDepartment.role = this.getParentManagerType(
              syndicate,
              order.syndicateDepartment.id
            );
          }
        });
        const result: {
          [id: number]: ManagerOverviewReport;
        } = orders.reduce(this.reduceOrders, {});
        syndicate.forEach(({ id, name, role }) => {
          if (!result[id]) {
            result[id] = new ManagerOverviewReportWithDefaultData(
              id,
              name,
              role
            );
          } else {
            result[id].name = name;
          }
        });
        return Object.keys(result).map(id => result[id]);
      })
    );
  }

  @ReusableStreamMethod
  public getManagersOverviewTotalsByTypes(): Observable<ManagerOverviewReport> {
    return this.getManagersOverviewData().pipe(
      map(managersArray =>
        managersArray.reduce(
          (
            accumulator: ManagerOverviewReportWithDefaultData,
            next: ManagerOverviewReport
          ): ManagerOverviewReport => accumulator.sum(next),
          new ManagerOverviewReportWithDefaultData()
        )
      )
    );
  }

  private reduceOrders(
    accumulator,
    next: GameDayModel.Order
  ): ManagerOverviewReport {
    const member = next.syndicateDepartment;
    const id = member.id;
    const amount = next.amount;
    const entry =
      accumulator[id] ||
      (accumulator[id] = new ManagerOverviewReportWithDefaultData(
        id,
        member.name,
        member.role as SyndicateRole
      ));

    switch (next.priority) {
      case GameDayModel.OrderPriority.retail:
        entry.retailAmount += amount;
        break;
      case GameDayModel.OrderPriority.institutional:
        entry.institutionalAmount += amount;
        break;
      case GameDayModel.OrderPriority.stock:
        entry.stockAmount += amount;
        break;
      default:
        break;
    }

    entry.total += amount;

    return accumulator;
  }

  private getParentManagerType(
    syndicate: GameDayModel.SyndicateMember[],
    departmentId: number
  ) {
    const parent = syndicate.find(
      (manager: GameDayModel.SyndicateMember) => manager.id === departmentId
    );
    return parent ? parent.role : ('' as SyndicateRole);
  }
}
